/// <reference types="@types/gapi" />
/// <reference types="@types/gapi.auth2" />

import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { environment } from '@environments/environment';
import { NEVER, from, Observable, of, throwError } from 'rxjs';
import { catchError, map, switchMap, take, tap } from 'rxjs/operators';
import { GoogleAuthResult, GoogleBusiness } from '@models/index';
import { NotAuthenticated } from '@reducers/gmb/gmb.reducer';
import { until } from '@utilities/function-utils';

const CLIENT_ID = '4852204945-8ksubdouvogp0rg53aagut65aud99etk.apps.googleusercontent.com';

@Injectable({
    providedIn: 'root'
})
export class GMBService {
    private gauth2: gapi.auth2.GoogleAuth | null = null;

    constructor(
        private http: HttpClient,
    ) {
    }

    // token should be populated when the app is re-opened from a successful Google login
    login(userId: string, token: string | null = null): Observable<boolean> {
        if (token == null) {
          this.login_redirect();
          return NEVER;
        }

        const url = `${environment.apiPath}/integrations/${userId}/google/token/`;

        return from(this.decodeAuthCode(token)).pipe(
            switchMap(code => this.http.post<boolean>(url, { code })),
            catchError(error =>{
                return throwError(error)
            })
        )
    }

    businesses(userId: string): Observable<GoogleBusiness[] | NotAuthenticated> {
        const url = `${environment.apiPath}/integrations/${userId}/google/business`;

        return this.http.get<GoogleBusiness[]>(url).pipe(
            switchMap(response => Array.isArray(response) ? of(response) : throwError({
                message: 'The server did not provide a list of businesses in its response.'
            })),
            catchError(error => {
              if ((error.error || {}).message == "NO_TOKENS") {
                return of<NotAuthenticated>(false);
              }
              return throwError(error)
            })
        );
    }

    refreshBusinesses(userId: string): Observable<GoogleBusiness[] | NotAuthenticated> {
        const url = `${environment.apiPath}/integrations/${userId}/google/business/refresh`;

        return this.http.post<GoogleBusiness[]>(url, {}).pipe(
            switchMap(response => Array.isArray(response) ? of(response) : throwError({
                message: 'The server did not provide a list of businesses in its response.'
            })),
            catchError(error => {
              if ((error.error || {}).message == "NO_TOKENS") {
                return of<NotAuthenticated>(false);
              }
              return throwError(error)
            })
        );
    }

    private async login_redirect() {
	const redirect_uri = encodeURIComponent(`${environment.gauthRedirectApiPath}/oauth/redirect.html`);
        const scope = [
          'profile',
          'email',
          encodeURIComponent("https://www.googleapis.com/auth/business.manage"),
        ].join(' ');
        const state = btoa(window.location.toString());

        location.replace(`https://accounts.google.com/o/oauth2/v2/auth?client_id=${CLIENT_ID}&redirect_uri=${redirect_uri}&scope=${scope}&response_type=code&access_type=offline&prompt=consent&state=${state}`);
    }

    decodeAuthCode(code: string): Observable<string> {
        const url = `${environment.gauthRedirectApiPath}/decode?code=${code}`;

        return this.http.get(url, { responseType: 'text' }).pipe(
            switchMap(response => typeof response == 'string' ? of(response) : throwError({
                message: 'The server did not respond with the authorization code.'
            })),
        );
    }
}
